import gql from 'graphql-tag';

/**
 * QUERY 1 — Lista de periodos (para el selector).
 * institutionId va PLANO (no hasheado). Devuelve solo cuestionarios anónimos
 * multi-respuesta con al menos 1 respuesta, ordenados por startDate desc.
 * sentSurveysCount = encuestas enviadas por correo (captura manual en la
 * asignación); null si no se capturó.
 */
const getAnonymousResultPeriods = gql`
  query GetAnonymousResultPeriods($institutionId: String!) {
    getAnonymousResultPeriods(institutionId: $institutionId) {
      allocationId
      questionnaireId
      questionnaireName
      status
      startDate
      dueDate
      totalResponses
      sentSurveysCount
    }
  }
`;

/**
 * QUERY 2 — Resultados de un periodo (por allocationId).
 *
 * - demographics (nivel raíz): cortes globales por key dinámica (gender,
 *   sucursal, etc.). Alimenta el filtro de Sucursal.
 * - categories: desglose por categoría (padre→hijas vía parentCategoryName),
 *   con su propio cruce demográfico. Alimenta el filtro de Dimensión.
 *
 * Semántica: averageScore = media de RESPUESTAS (no personas), escala 0–10,
 * null si no hay respuestas puntuables. count = nº de ENCUESTADOS.
 *
 * filters es opcional: recorta la población antes de agregar, así que
 * totalResponses, demographics y los averageScore ya vienen re-escopados.
 */
const getAnonymousResults = gql`
  query GetAnonymousResults($allocationId: Int!, $filters: [DemographicFilterInput!]) {
    getAnonymousResults(allocationId: $allocationId, filters: $filters) {
      questionnaireName
      totalResponses
      overallAverageScore
      status
      fromCache
      computedAt
      demographics {
        key
        label
        values {
          value
          count
          averageScore
        }
      }
      categories {
        categoryName
        parentCategoryName
        averageScore
        demographics {
          key
          label
          values {
            value
            count
            averageScore
          }
        }
      }
      allocationId
      questionnaireId
    }
  }
`;

/**
 * QUERY 3 — Cruce de dos ejes demográficos por dimensión (tabla combinada).
 *
 * keyA / keyB son keys EXACTAS del catálogo (gender, age-range, branch-office, ...).
 * Por cada categoría (dimensión padre→hija) devuelve:
 * - cells: interior de la matriz (valueA ∧ valueB); matriz DISPERSA (faltan las
 *   combinaciones sin encuestados) y averageScore puede ser null.
 * - marginalsA: columna GLOBAL (agregado por valueA), ya ponderada.
 * - marginalsB: fila PROMEDIO (agregado por valueB), ya ponderada.
 * - overallCount / overallAverageScore: esquina (agregado total del cruce).
 *
 * NO promediar los bordes a mano: marginals y overall ya vienen ponderados.
 */
const getAnonymousResultsCross = gql`
  query GetAnonymousResultsCross($allocationId: Int!, $keyA: String!, $keyB: String!) {
    getAnonymousResultsCross(allocationId: $allocationId, keyA: $keyA, keyB: $keyB) {
      allocationId
      keyA
      keyB
      labelA
      labelB
      totalResponses
      categories {
        categoryName
        parentCategoryName
        overallCount
        overallAverageScore
        cells {
          valueA
          valueB
          count
          averageScore
        }
        marginalsA {
          value
          count
          averageScore
        }
        marginalsB {
          value
          count
          averageScore
        }
      }
    }
  }
`;

/**
 * QUERY 4 — Estructura del cuestionario por id (para el catálogo de dimensiones).
 *
 * Devuelve las categorías planas; el árbol se arma con parent:
 * dimensión (parent === null) → categoría (parent.id === dimensión.id) → preguntas.
 */
const getQuestionnaireById = gql`
  query QuestionnaireById($id: Int!, $args: FilterParamsInput) {
    questionnaireById(id: $id, args: $args) {
      id
      name
      categories {
        id
        name
        description
        parent {
          id
        }
        questions {
          id
          order
          description
        }
      }
    }
  }
`;

export { getAnonymousResultPeriods, getAnonymousResults, getAnonymousResultsCross, getQuestionnaireById };
